import { TypePolicies } from "@apollo/client";
import { SuggestedSlotsByDate } from "@clockwise/schema";

/**
 * Inform the Apollo cache how to merge certain fields on certain types, so that `fetchMore`
 * behavior works correctly.
 */
export const typePolicies: TypePolicies = {
  Viewer: {
    fields: {
      schedulingLinkDetails: {
        // https://github.com/apollographql/apollo-client/issues/7314 Explains the syntax
        // for using nested fields as part of keyArgs. [field1, field2, fieldWithNested, [nestedField1, ...]]
        keyArgs: ["linkName", "slug", "body", ["timeZone"]],
      },
    },
  },
  PublicBooking: {
    fields: {
      schedulingLinkDetails: {
        keyArgs: ["timeZone"],
      },
    },
  },
  SchedulingLinkDetails: {
    merge: true,
    fields: {
      suggestedSlotsByDate: {
        merge(existing: SuggestedSlotsByDate[], incoming: SuggestedSlotsByDate[], { readField }) {
          // This is basically a reducer for concatenating new suggestedSlotsByDate into
          // matching SchedulingLinkDetails object.
          const newSlotsByDate = existing ? existing.slice(0) : [];
          const byDateMap: Record<string, number> = {};
          if (existing) {
            existing.forEach((slotsByDate, index) => {
              const date = readField<string>("date", slotsByDate);
              if (date) {
                // Needed just because readField can technically return undefined. Should be defined.
                byDateMap[date] = index;
              }
            });
          }
          // For each new object, if we already have that date replace the previous times,
          // else concatenate onto existing suggestedSlotsByDate.
          incoming.forEach((slotsByDate) => {
            const date = readField<string>("date", slotsByDate);
            if (!date) {
              return; // Needed just because readField can technically return undefined. Should be defined.
            }
            const index = byDateMap[date];
            if (typeof index === "number") {
              // Replace existing slots with new possibily updated slots for that date.
              newSlotsByDate[index] = slotsByDate;
            } else {
              newSlotsByDate.push(slotsByDate);
            }
          });

          // Sort by date.
          newSlotsByDate.sort((a, b) =>
            (readField<string>("date", a) || "") < (readField<string>("date", b) || "") ? -1 : 1,
          );
          return newSlotsByDate;
        },
      },
    },
  },
};
